/**
 * GruposSubtab — CRUD grupos + UI pa asignar topics y miembros.
 *
 * Patrón: lista izquierda de grupos, panel derecho con detalle del seleccionado
 * (topics asignados + miembros). Diálogo simple pa crear grupo nuevo.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import {
  useAddGroupMember,
  useAddGroupTopic,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useGroupDetail,
  useGroups,
  useRemoveGroupMember,
  useRemoveGroupTopic,
  useTopics,
} from "./api";

type UserMini = {
  id: string;
  email: string;
  nombre_completo: string | null;
  rol_nombre: string | null;
};

function useUsersActivos() {
  return useQuery({
    queryKey: ["notif-settings", "users-activos"],
    queryFn: () =>
      apiCall<{ items: UserMini[] }>("get", "/api/v1/users", {
        searchParams: { activo: true, size: 200 },
      }),
  });
}

export function GruposSubtab() {
  const groupsQ = useGroups();
  const topicsQ = useTopics();
  const usersQ = useUsersActivos();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  // rc46 — edit grupo inline
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const createMut = useCreateGroup();
  const updateMut = useUpdateGroup();
  const deleteMut = useDeleteGroup();

  function startEdit(id: string, nombre: string, descripcion: string | null) {
    setEditingId(id);
    setEditName(nombre);
    setEditDesc(descripcion ?? "");
    setCreating(false);  // cierra el form de crear si estaba abierto
  }

  async function handleEdit() {
    if (!editingId || !editName.trim()) return;
    try {
      await updateMut.mutateAsync({
        id: editingId,
        data: {
          nombre: editName.trim(),
          descripcion: editDesc.trim() || null,
        },
      });
      toast.success("Grupo actualizado");
      setEditingId(null);
    } catch (e: any) {
      toast.error(e?.message ?? "Error al actualizar");
    }
  }

  async function handleCreate() {
    if (!newName.trim()) return;
    try {
      const grp = await createMut.mutateAsync({
        nombre: newName.trim(),
        descripcion: newDesc.trim() || undefined,
      });
      toast.success(`Grupo "${grp.nombre}" creado`);
      setCreating(false);
      setNewName("");
      setNewDesc("");
      setSelectedId(grp.id);
    } catch (e: any) {
      toast.error(e?.message ?? "Error al crear");
    }
  }

  async function handleDelete(id: string, nombre: string) {
    if (!confirm(`¿Eliminar el grupo "${nombre}"? Esto desuscribe a sus miembros de los topics asignados al grupo.`)) {
      return;
    }
    try {
      await deleteMut.mutateAsync(id);
      toast.success("Grupo eliminado");
      if (selectedId === id) setSelectedId(null);
    } catch (e: any) {
      toast.error(e?.message ?? "Error al eliminar");
    }
  }

  if (groupsQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={16} className="animate-spin" />
      </div>
    );
  }

  const groups = groupsQ.data ?? [];

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {/* Lista izquierda */}
      <div className="md:col-span-1">
        <div className="rounded-lg border bg-card">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Grupos ({groups.length})
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreating((s) => !s)}
            >
              <Plus size={12} />
              Nuevo
            </Button>
          </div>

          {creating && (
            <div className="space-y-2 border-b bg-muted/30 p-3">
              <input
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                placeholder="Nombre (ej. Cobranza)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
              <input
                className="w-full rounded-md border bg-background px-2 py-1.5 text-xs"
                placeholder="Descripción (opcional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={!newName.trim() || createMut.isPending}
                >
                  Crear
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {groups.length === 0 && !creating && (
            <div className="p-4 text-center text-xs text-muted-foreground">
              No hay grupos todavía. Crea uno arriba.
            </div>
          )}
          <div className="divide-y">
            {groups.map((g) => {
              const isEditing = editingId === g.id;
              if (isEditing) {
                return (
                  <div key={g.id} className="space-y-2 border-l-2 border-primary bg-muted/30 p-3">
                    <input
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                      placeholder="Nombre"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                    />
                    <input
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-xs"
                      placeholder="Descripción (opcional)"
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleEdit}
                        disabled={!editName.trim() || updateMut.isPending}
                      >
                        Guardar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={g.id}
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-muted/40 ${
                    selectedId === g.id ? "bg-muted/50" : ""
                  }`}
                  onClick={() => setSelectedId(g.id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{g.nombre}</div>
                    {g.descripcion && (
                      <div className="text-[11px] text-muted-foreground">{g.descripcion}</div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEdit(g.id, g.nombre, g.descripcion);
                    }}
                    title="Editar grupo"
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(g.id, g.nombre);
                    }}
                    title="Eliminar grupo"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detalle derecha */}
      <div className="md:col-span-2">
        {selectedId ? (
          <GroupDetailPanel
            groupId={selectedId}
            topics={topicsQ.data ?? []}
            users={usersQ.data?.items ?? []}
          />
        ) : (
          <div className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
            Selecciona un grupo para ver sus topics y miembros.
          </div>
        )}
      </div>
    </div>
  );
}

function GroupDetailPanel({
  groupId,
  topics,
  users,
}: {
  groupId: string;
  topics: { codigo: string; nombre: string; categoria: string }[];
  users: UserMini[];
}) {
  const detailQ = useGroupDetail(groupId);
  const addTopicMut = useAddGroupTopic();
  const removeTopicMut = useRemoveGroupTopic();
  const addMemberMut = useAddGroupMember();
  const removeMemberMut = useRemoveGroupMember();

  const [topicToAdd, setTopicToAdd] = useState("");
  const [userToAdd, setUserToAdd] = useState("");

  if (detailQ.isLoading || !detailQ.data) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={16} className="animate-spin" />
      </div>
    );
  }
  const grp = detailQ.data;

  const assignedTopicCodes = new Set(grp.topics.map((t) => t.topic));
  const availableTopics = topics.filter((t) => !assignedTopicCodes.has(t.codigo));

  const memberIds = new Set(grp.members.map((m) => m.user_id));
  const availableUsers = users.filter((u) => !memberIds.has(u.id));

  async function handleAddTopic() {
    if (!topicToAdd) return;
    try {
      await addTopicMut.mutateAsync({ groupId, topic: topicToAdd, canal: "push" });
      setTopicToAdd("");
    } catch (e: any) {
      toast.error(e?.message ?? "Error al agregar topic");
    }
  }

  async function handleAddMember() {
    if (!userToAdd) return;
    try {
      await addMemberMut.mutateAsync({ groupId, userId: userToAdd });
      setUserToAdd("");
    } catch (e: any) {
      toast.error(e?.message ?? "Error al agregar miembro");
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="rounded-lg border bg-card p-3">
        <div className="text-base font-semibold">{grp.nombre}</div>
        {grp.descripcion && (
          <div className="text-xs text-muted-foreground">{grp.descripcion}</div>
        )}
      </div>

      {/* Topics asignados */}
      <div className="rounded-lg border bg-card">
        <div className="border-b px-3 py-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          Topics asignados ({grp.topics.length})
        </div>
        <div className="space-y-2 p-3">
          {grp.topics.length === 0 && (
            <div className="text-xs text-muted-foreground">Sin topics asignados todavía.</div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {grp.topics.map((gt) => {
              const t = topics.find((x) => x.codigo === gt.topic);
              return (
                <span
                  key={gt.id}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-muted px-2 py-0.5 text-xs"
                >
                  {t?.nombre ?? gt.topic}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      removeTopicMut.mutate({
                        groupId,
                        topic: gt.topic,
                        canal: gt.canal,
                      })
                    }
                    title="Quitar topic"
                  >
                    <X size={11} />
                  </button>
                </span>
              );
            })}
          </div>

          {availableTopics.length > 0 && (
            <div className="flex gap-2 pt-2">
              <select
                className="flex-1 rounded-md border bg-background px-2 py-1.5 text-xs"
                value={topicToAdd}
                onChange={(e) => setTopicToAdd(e.target.value)}
              >
                <option value="">— Agregar topic —</option>
                {availableTopics.map((t) => (
                  <option key={t.codigo} value={t.codigo}>
                    {t.categoria} · {t.nombre}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={handleAddTopic}
                disabled={!topicToAdd || addTopicMut.isPending}
              >
                <Plus size={12} />
                Agregar
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Miembros */}
      <div className="rounded-lg border bg-card">
        <div className="border-b px-3 py-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          Miembros ({grp.members.length})
        </div>
        <div className="space-y-2 p-3">
          {grp.members.length === 0 && (
            <div className="text-xs text-muted-foreground">Sin miembros todavía.</div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {grp.members.map((m) => {
              const u = users.find((x) => x.id === m.user_id);
              return (
                <span
                  key={m.id}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-muted px-2 py-0.5 text-xs"
                >
                  {u?.nombre_completo || u?.email || m.user_id}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      removeMemberMut.mutate({ groupId, userId: m.user_id })
                    }
                    title="Quitar miembro"
                  >
                    <X size={11} />
                  </button>
                </span>
              );
            })}
          </div>

          {availableUsers.length > 0 && (
            <div className="flex gap-2 pt-2">
              <select
                className="flex-1 rounded-md border bg-background px-2 py-1.5 text-xs"
                value={userToAdd}
                onChange={(e) => setUserToAdd(e.target.value)}
              >
                <option value="">— Agregar miembro —</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre_completo || u.email}
                    {u.rol_nombre ? ` (${u.rol_nombre})` : ""}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={handleAddMember}
                disabled={!userToAdd || addMemberMut.isPending}
              >
                <Plus size={12} />
                Agregar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
